/**
 * Evaluators tab - list view for custom evaluator sets.
 *
 * Surfaces the org's scoring criteria and lets users create, inspect, activate,
 * or delete evaluator sets.
 */
import { useMemo, useState } from 'react';
import { AlertCircle, Loader2, Plus, Trash2 } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataSurface } from '@/components/platform/DataSurface';
import { MetricStrip } from '@/components/platform/MetricStrip';
import { TooltipLabel } from '@/components/ui/TooltipLabel';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useOrganization } from '@/hooks/useOrganization';
import { toast } from '@/hooks/use-toast';
import {
  useCustomEvalSets,
  useDeleteCustomEvalSet,
  usePublishCustomEvalVersion,
} from '@/hooks/useCustomEvals';
import type {
  CustomEvalSetResponse,
  CustomEvalSetVersionResponse,
} from '@/lib/api/customEvals';
import { cn } from '@/lib/utils';
import { CustomEvalBuilderDialog } from './CustomEvalBuilderDialog';
import { CustomEvalDetailDialog } from './CustomEvalDetailDialog';

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '';
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return '';
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'just now';
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

interface StatusBadgeInfo {
  label: 'Active' | 'Draft' | 'Archived';
  variant: 'default' | 'secondary' | 'outline';
  version: CustomEvalSetVersionResponse | null;
}

function deriveStatus(set: CustomEvalSetResponse): StatusBadgeInfo {
  const versions = set.versions ?? [];

  if (set.active_version_id) {
    const active =
      set.active_version ??
      versions.find((v) => v.id === set.active_version_id) ??
      null;
    return {
      label: 'Active',
      variant: 'default',
      version: active,
    };
  }

  const latestDraft = versions
    .filter((v) => v.status === 'draft')
    .sort((a, b) => b.version_number - a.version_number)[0];
  if (latestDraft) {
    return {
      label: 'Draft',
      variant: 'secondary',
      version: latestDraft,
    };
  }

  return { label: 'Archived', variant: 'outline', version: null };
}

function deriveDescription(set: CustomEvalSetResponse): string {
  if (set.description && set.description.trim()) return set.description;
  const versions = set.versions ?? [];
  const candidate = set.active_version ?? versions[0];
  const firstCriterion = candidate?.criteria?.[0];
  const meta = firstCriterion?.metadata;
  if (meta && typeof meta === 'object' && !Array.isArray(meta)) {
    const userDescription = (meta as Record<string, unknown>).user_description;
    if (typeof userDescription === 'string' && userDescription.trim()) {
      return userDescription.trim();
    }
  }
  return '';
}

function getCriteriaCount(set: CustomEvalSetResponse): number {
  if (set.active_version?.criteria) return set.active_version.criteria.length;
  const versions = set.versions ?? [];
  return versions[0]?.criteria?.length ?? 0;
}

export function EvaluatorsTab() {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;
  const [openSetId, setOpenSetId] = useState<string | null>(null);
  const [builderOpen, setBuilderOpen] = useState(false);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  } = useCustomEvalSets(orgId, { includeVersions: true });

  const sortedSets = useMemo(() => {
    const sets = data?.sets ?? [];
    return [...sets].sort((a, b) => {
      const aDate = a.updated_at ?? a.created_at ?? '';
      const bDate = b.updated_at ?? b.created_at ?? '';
      return bDate.localeCompare(aDate);
    });
  }, [data?.sets]);

  const currentActiveSet = useMemo(
    () => sortedSets.find((s) => s.active_version_id != null) ?? null,
    [sortedSets],
  );

  const activeCount = useMemo(
    () => sortedSets.filter((set) => set.active_version_id != null).length,
    [sortedSets],
  );

  const draftCount = useMemo(
    () => sortedSets.filter((set) => deriveStatus(set).label === 'Draft').length,
    [sortedSets],
  );

  const criteriaCount = useMemo(
    () => sortedSets.reduce((sum, set) => sum + getCriteriaCount(set), 0),
    [sortedSets],
  );

  if (!orgId) {
    return (
      <DataSurface>
        <div className="flex items-center justify-center py-16">
          <p className="text-sm text-muted-foreground">Select an organization to continue.</p>
        </div>
      </DataSurface>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <TooltipLabel
            label="Scoring criteria"
            description="Active evaluators score conversations and simulations against the outcomes you care about."
            className="text-sm font-medium text-foreground"
          />
        </div>
        {!isLoading && sortedSets.length > 0 ? (
          <Button size="sm" onClick={() => setBuilderOpen(true)}>
            <Plus className="h-4 w-4" />
            Create Evaluator
          </Button>
        ) : null}
      </div>

      {isLoading ? (
        <DataSurface>
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        </DataSurface>
      ) : isError ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Could not load evaluators</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{error instanceof Error ? error.message : 'Unknown error'}</p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => refetch()}
              disabled={isRefetching}
            >
              {isRefetching ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Retrying
                </span>
              ) : (
                'Retry'
              )}
            </Button>
          </AlertDescription>
        </Alert>
      ) : sortedSets.length === 0 ? (
        <DataSurface>
          <div className="mx-auto flex max-w-xl flex-col items-center justify-center px-6 py-16 text-center">
            <p className="text-sm font-medium text-foreground">No evaluators yet</p>
            <p className="mt-2 max-w-md text-sm leading-6 text-muted-foreground">
              Create criteria for judging whether Hobbes handled a conversation well.
            </p>
            <Button className="mt-5" size="sm" onClick={() => setBuilderOpen(true)}>
              <Plus className="h-4 w-4" />
              Create Evaluator
            </Button>
          </div>
        </DataSurface>
      ) : (
        <div className="space-y-4">
          <MetricStrip
            items={[
              {
                label: 'Evaluators',
                value: sortedSets.length,
                meta: `${activeCount} active`,
              },
              {
                label: 'Criteria',
                value: criteriaCount,
                meta: 'Used for scoring',
              },
              {
                label: 'Drafts',
                value: draftCount,
                meta: 'Not active yet',
              },
            ]}
            className="lg:grid-cols-3"
          />
          <DataSurface>
            <Table>
              <TableHeader>
                <TableRow className="border-b border-border hover:bg-transparent">
                  <TableHead className="h-9 px-4 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Evaluator
                  </TableHead>
                  <TableHead className="h-9 px-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Status
                  </TableHead>
                  <TableHead className="h-9 px-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Criteria
                  </TableHead>
                  <TableHead className="h-9 px-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Last Updated
                  </TableHead>
                  <TableHead className="h-9 px-4 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedSets.map((set) => {
                  const otherActiveSetName =
                    currentActiveSet && currentActiveSet.id !== set.id
                      ? currentActiveSet.name
                      : null;
                  return (
                    <EvaluatorRow
                      key={set.id}
                      set={set}
                      organizationId={orgId}
                      onOpen={() => setOpenSetId(set.id)}
                      otherActiveSetName={otherActiveSetName}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </DataSurface>
        </div>
      )}

      <CustomEvalDetailDialog
        open={openSetId !== null}
        onOpenChange={(next) => {
          if (!next) setOpenSetId(null);
        }}
        organizationId={orgId}
        setId={openSetId}
        otherActiveSetName={
          currentActiveSet && currentActiveSet.id !== openSetId
            ? currentActiveSet.name
            : null
        }
      />

      <CustomEvalBuilderDialog
        open={builderOpen}
        onOpenChange={setBuilderOpen}
        organizationId={orgId}
        onCreated={(setId) => setOpenSetId(setId)}
      />
    </div>
  );
}

interface EvaluatorRowProps {
  set: CustomEvalSetResponse;
  organizationId: string;
  onOpen: () => void;
  otherActiveSetName: string | null;
}

function EvaluatorRow({ set, organizationId, onOpen, otherActiveSetName }: EvaluatorRowProps) {
  const status = deriveStatus(set);
  const description = deriveDescription(set);
  const criteriaCount = getCriteriaCount(set);
  const updatedRelative = formatRelative(set.updated_at ?? set.created_at);

  const publishMutation = usePublishCustomEvalVersion();
  const deleteMutation = useDeleteCustomEvalSet();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const canPublish =
    !set.active_version_id && status.version != null && status.version.status === 'draft';

  const handlePublish = () => {
    if (!status.version) return;
    publishMutation.mutate(
      {
        setId: set.id,
        versionId: status.version.id,
        organizationId,
      },
      {
        onSuccess: () => {
          toast({
            title: 'Evaluator activated',
            description: `"${set.name}" is now active.`,
          });
        },
        onError: (err) => {
          toast({
            title: 'Could not activate evaluator',
            description: err instanceof Error ? err.message : 'Unknown error',
            variant: 'destructive',
          });
        },
      },
    );
  };

  const handleDelete = () => {
    deleteMutation.mutate(
      { setId: set.id, organizationId },
      {
        onSuccess: () => {
          setConfirmDeleteOpen(false);
          toast({
            title: 'Evaluator deleted',
            description: `"${set.name}" has been removed.`,
          });
        },
        onError: (err) => {
          setConfirmDeleteOpen(false);
          toast({
            title: 'Could not delete evaluator',
            description: err instanceof Error ? err.message : 'Unknown error',
            variant: 'destructive',
          });
        },
      },
    );
  };

  return (
    <TableRow
      onClick={onOpen}
      className="cursor-pointer border-b border-border transition-colors hover:bg-muted/30"
    >
      <TableCell className="px-4 py-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-foreground">{set.name}</div>
          {description ? (
            <p className="mt-1 line-clamp-1 max-w-xl text-xs text-muted-foreground">{description}</p>
          ) : null}
        </div>
      </TableCell>

      <TableCell className="px-3 py-3">
        <Badge
          variant={status.variant}
          className={cn(
            'text-[11px] font-medium',
            status.label === 'Active' && 'border-success/30 bg-success/10 text-success hover:bg-success/10',
            status.label === 'Draft' && 'border-border bg-muted/60 text-muted-foreground hover:bg-muted/60',
          )}
        >
          {status.label}
        </Badge>
      </TableCell>

      <TableCell className="px-3 py-3 text-sm text-muted-foreground">
        {criteriaCount}
      </TableCell>

      <TableCell className="px-3 py-3 text-sm text-muted-foreground">
        {updatedRelative || '-'}
      </TableCell>

      <TableCell
        className="px-4 py-3"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-end gap-2">
          {canPublish ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={publishMutation.isPending}
                >
                  {publishMutation.isPending ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Activating
                    </span>
                  ) : (
                    'Activate'
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Activate this evaluator?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Activating <span className="font-semibold">{set.name}</span> will make it the
                    live evaluator
                    {otherActiveSetName ? (
                      <>
                        {' '}and archive the currently active{' '}
                        <span className="font-semibold">{otherActiveSetName}</span>
                      </>
                    ) : null}
                    . New conversations will be scored against it from now on.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handlePublish}>Activate</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : null}

          <AlertDialog
            open={confirmDeleteOpen}
            onOpenChange={(next) => {
              if (deleteMutation.isPending) return;
              setConfirmDeleteOpen(next);
            }}
          >
            <AlertDialogTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                title="Delete evaluator"
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5" />
                )}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this evaluator?</AlertDialogTitle>
                <AlertDialogDescription>
                  <span className="font-semibold">{set.name}</span> and all of its versions
                  will be permanently deleted. Conversations will no longer be scored against
                  it. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={(e) => {
                    e.preventDefault();
                    handleDelete();
                  }}
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Deleting
                    </span>
                  ) : (
                    'Delete evaluator'
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </TableCell>
    </TableRow>
  );
}

export default EvaluatorsTab;
